import React, { useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import InfoIcon from "@material-ui/icons/Info";
import PersonIcon from "@material-ui/icons/Person";
import useBDocTranslator from "../hooks/useBDocTranslator";
import { useHistory } from "react-router-dom";
import useUserState from "../hooks/useUserState";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    position: "relative",
    paddingRight: theme.spacing(6),
  },
  titleText: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 600,
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.primary.contrastText,
  },
  content: {
    padding: theme.spacing(3),
    textAlign: "center",
    minHeight: 120,
  },
  infoBox: {
    backgroundColor: "#f5f5f5",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
    border: `1px solid ${theme.palette.divider}`,
  },
  messageText: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  chip: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
    fontWeight: 500,
  },
  actions: {
    padding: theme.spacing(2, 3),
    justifyContent: "center",
  },
  actionButton: {
    minWidth: 200,
    padding: theme.spacing(1.5, 3),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "1rem",
  },
  dialog: {
    "& .MuiDialog-paper": {
      minWidth: 400,
      maxWidth: 500,
      [theme.breakpoints.down("sm")]: {
        margin: theme.spacing(2),
        width: `calc(100% - ${theme.spacing(4)}px)`,
      },
    },
  },
}));

const ModalCompleteInfo = ({ open, close }) => {
  const classes = useStyles();
  const { t } = useBDocTranslator();
  let history = useHistory();
  const userLocalState = useUserState();

  useEffect(() => {
    if (!!open) {
      window.sessionStorage.setItem("showCompleteInfo", true);
    }
  }, [open]);

  const isImage = useMemo(() => {
    if (!userLocalState) return false;
    if (
      userLocalState[0] &&
      userLocalState[0].user &&
      userLocalState[0].user.completeInfo &&
      userLocalState[0].user.completeInfo.includes("image")
    ) {
      return true;
    }
    return false;
  }, [userLocalState]);

  return (
    <Dialog
      open={open}
      onClose={close}
      className={classes.dialog}
      maxWidth="sm"
      fullWidth
      aria-labelledby="complete-info-dialog-title"
      aria-describedby="complete-info-dialog-description"
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6" className={classes.titleText}>
          <InfoIcon />
          {t("You have to complete your user information")}
        </Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={close}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Box className={classes.infoBox}>
          <Typography 
            variant="body1" 
            className={classes.messageText}
            id="complete-info-dialog-description"
          >
            {t("You have to complete your user information")}
          </Typography>
          
          {isImage && (
            <Box mt={2}>
              <Chip
                icon={<PersonIcon />}
                label={t("Add profile image")}
                className={classes.chip}
                size="medium"
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => history.push("/patient/editProfile")}
          className={classes.actionButton}
          startIcon={<PersonIcon />}
        >
          {t("Go To Edit Profile")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalCompleteInfo;
